import { ApolloQueryResult } from '@apollo/client';
import { useMemo } from 'react';

import { ById, ByName, Category } from '@/types/categories';
import { HookResult } from './apolloClient';
import { RGetCategoriesQuery, useGetCategoriesQueryRemote } from '@/store/v2';

interface CategoriesResponse extends HookResult {
  categories: ReadonlyArray<Category>;
  categoriesById: ById;
  categoriesByName: ByName;
  subjectsById: ById;
  refetch: () => Promise<ApolloQueryResult<RGetCategoriesQuery>>;
  loading: boolean;
}

export const useCategories = (): CategoriesResponse => {
  const { data, error, refetch, loading, networkStatus } = useGetCategoriesQueryRemote();

  /**
   * The schema types every Category and Subject field as nullable, so incomplete rows are
   * dropped here instead of widening `Category` for all of its consumers. Memoized because the
   * mapping allocates, and consumers use the result as a hook dependency.
   */
  const categories: ReadonlyArray<Category> = useMemo(
    () =>
      (data?.getCategories ?? []).flatMap((category) =>
        category?.id != null && category.name != null && category.urlKey != null
          ? [
              {
                id: category.id,
                name: category.name,
                urlKey: category.urlKey,
                subjects: (category.subjects ?? []).flatMap((subject) =>
                  subject?.id != null && subject.name != null && subject.urlKey != null
                    ? [{ id: subject.id, name: subject.name, urlKey: subject.urlKey }]
                    : [],
                ),
              },
            ]
          : [],
      ),
    [data?.getCategories],
  );

  const categoriesById = categories.reduce<ById>(
    (accumulator, currentValue) => ({
      ...accumulator,
      [currentValue.id]: {
        name: currentValue.name,
        urlKey: currentValue.urlKey,
      },
    }),
    {},
  );

  const categoriesByName = categories.reduce<ByName>(
    (accumulator, currentValue) => ({
      ...accumulator,
      [currentValue.name]: {
        name: currentValue.name,
        id: currentValue.id,
        urlKey: currentValue.urlKey,
      },
    }),
    {},
  );

  const subjectsById = categories.reduce<ById>((accumulator, currentValue) => {
    currentValue.subjects.forEach((subject) => {
      accumulator[subject.id] = {
        name: subject.name,
        urlKey: subject.urlKey,
      };
    });

    return accumulator;
  }, {});

  return {
    categories,
    categoriesById,
    categoriesByName,
    subjectsById,
    error,
    refetch,
    loading,
    networkStatus,
  };
};
